/* Landing — SeoToolsShowcase + UtilityWidgetsShowcase extracted into
 * a separate lazy chunk. v60.26.
 *
 * Both sections live well below the LCP region — SeoToolsShowcase is
 * the 4th section, UtilityWidgetsShowcase the 5th, both wrapped in
 * existing `<DeferUntilVisible>` placeholders in Landing.jsx. They
 * previously shipped inside the homepage's main chunk because they
 * were defined inline in `pages/Landing.jsx`.
 *
 * Moving them here and lazy-importing from Landing trims another
 * ~170 lines of JSX (estimated 6-10 KB gzipped) out of the homepage's
 * initial bundle. Same pattern as v60.25's `LandingFooter.jsx` split.
 *
 * Dependencies (Link, useI18n, lucide ArrowUpRight, content_i18n
 * helpers, catalog_counts) are duplicate-imported here rather than
 * passed via props. webpack tree-shakes shared modules across both
 * chunks anyway.
 */
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { seoToolTitleForLang } from "@/lib/content_i18n";
import { WIDGET_COUNT, SEO_TOOL_COUNT, widgetCountWord, seoToolCountWord } from "@/lib/catalog_counts";

/* SEO Tools showcase — homepage discovery surface for the 8 free SEO
 * checker tools shipped in Phase 1. Pure marketing block — each card
 * deep-links to its own SEO-optimized landing page.
 *
 * Layout note: the 8 real tools fill a clean 4×2 grid. A separate
 * "discovery row" below them surfaces an "All SEO Tools" CTA flanked by
 * two teaser tiles (Coming Soon + Suggest a tool) so the section never
 * shows a half-empty row with grey background bleed-through. As we add
 * new tools they slot into the discovery row (or graduate into the main
 * grid). */
export const SeoToolsShowcase = () => {
  const { lang } = useI18n();
  // Top-8 home cards selected by `homepagePriority` in seo_tools.js
  // (lower = higher priority). Two new tools (SERP Checker, Index
  // Checker) bumped to priority 1+2 to feature them on launch; the
  // /tools index page still shows all 10. Always 8 cards for the
  // clean 4×2 grid layout.
  const tools = [
    { slug: "rank", title: "FeedPulse Traffic Rank", desc: "The open Alexa rank alternative. Free for every website on Earth.", tag: "Most popular" },
    { slug: "serp-checker", title: "SERP Position Checker", desc: "Free Google rank checker — bulk 3 keywords × 30 countries × desktop/mobile.", tag: "New · launched" },
    { slug: "indexed-checker", title: "Google Index Checker", desc: "Is your page indexed by Google? Check one URL or up to 5 at once.", tag: "New · launched" },
    { slug: "meta-tag-generator", title: "Free Meta Tag Generator", desc: "Generate <title>, meta description, Open Graph + Twitter Card tags in 20 seconds.", tag: "New · 8.1K/mo" },
    { slug: "schema-markup-generator", title: "Free Schema Markup Generator", desc: "JSON-LD for Article, Product, FAQ, LocalBusiness — Google Rich Results ready.", tag: "New · 5.4K/mo" },
    { slug: "ai-audit", title: "AI SEO Audit", desc: "Paste any URL → 30-second GPT-5.2 audit. A paid product everywhere else.", tag: "AI", noI18n: true },
    { slug: "da-checker", title: "Domain Authority Checker", desc: "Free DA checker, Moz-powered. Includes embeddable badge.", tag: "Free DA" },
    { slug: "backlinks", title: "Backlinks Checker", desc: "Free backlinks checker — live 'X sites link here' counter.", tag: "" },
  ];
  return (
    <section id="seo-tools" className="bg-zinc-50 border-y border-zinc-200" data-testid="seo-tools-showcase">
      <div className="max-w-7xl mx-auto px-6 py-24 lg:py-32">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 mb-3">/ free seo tools</div>
        <div className="grid lg:grid-cols-12 gap-8 mb-12">
          <h2 className="lg:col-span-7 font-display text-4xl lg:text-5xl font-black tracking-tighter">
            {seoToolCountWord()} free SEO tools.<br/>One free dashboard. <span className="text-klein">Zero signup.</span>
          </h2>
          <p className="lg:col-span-5 text-zinc-600 leading-relaxed self-end">
            SERP Position Checker · Google Index Checker · Meta Tag Generator · Schema Markup Generator · FeedPulse Traffic Rank · Domain Authority · Domain Rating · Site Trust Score · Lighthouse · Domain Age · Backlinks Checker · AI SEO Audit. Each tool ships with a free embeddable badge or copy-paste output so you can flex your scores on your own site. ToS-safe, SerpAPI + Moz + OpenPageRank + WHOIS + PageSpeed Insights powered.
          </p>
        </div>
        {/* Main grid — 8 of 10 cards, fills a clean 4×2 without orphan rows.
           Domain Age + Lighthouse + Page Speed live on /tools alone. */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-200">
          {tools.map((t) => (
            <Link
              key={t.slug}
              to={`/${t.slug}`}
              className="bg-white p-6 hover:bg-klein hover:text-white transition-colors group block"
              data-testid={`home-seo-tool-${t.slug}`}
            >
              {t.tag && (
                <div className="text-[10px] font-mono uppercase tracking-widest text-klein group-hover:text-emerald-300 mb-3">
                  {t.tag}
                </div>
              )}
              <h3 className="font-display text-xl font-black tracking-tight mb-2">
                {t.noI18n ? t.title : seoToolTitleForLang(t.slug, lang, t.title)}
              </h3>
              <p className="text-sm text-zinc-600 group-hover:text-zinc-100 leading-relaxed">{t.desc}</p>
            </Link>
          ))}
        </div>
        {/* Discovery row — 2 teaser tiles + a "see all 10" CTA. */}
        <div className="mt-px grid sm:grid-cols-3 gap-px bg-zinc-200" data-testid="seo-tools-discovery-row">
          {/* Left teaser — Lighthouse / speed / domain age (not in top 8) */}
          <Link to="/lighthouse" className="bg-white p-6 hover:bg-zinc-50 transition-colors group" data-testid="seo-tools-spotlight-lighthouse">
            <div className="flex items-center gap-3 mb-2">
              <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-amber-50 text-amber-600">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M12 6v6l4 2"/>
                </svg>
              </span>
              <div className="text-[10px] font-mono uppercase tracking-widest text-amber-600">Also free</div>
            </div>
            <h3 className="font-display text-lg font-black tracking-tight mb-1">Lighthouse Score Badge</h3>
            <p className="text-sm text-zinc-600 leading-relaxed">Shields.io for websites. Scores come straight from PageSpeed Insights.</p>
          </Link>
          {/* Center CTA — see all 10 */}
          <Link
            to="/tools"
            className="bg-klein text-white p-6 hover:bg-[#0A0A0A] transition-colors group flex flex-col justify-center items-center text-center"
            data-testid="home-seo-tool-all"
          >
            <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-300 mb-2 flex items-center gap-1.5">
              <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z"/>
              </svg>
              Full directory
            </div>
            <h3 className="font-display text-2xl lg:text-3xl font-black tracking-tighter mb-1">
              See all {SEO_TOOL_COUNT} tools <span aria-hidden="true">→</span>
            </h3>
            <p className="text-sm text-emerald-200 leading-relaxed">Every SEO metric. One dashboard. Zero signup.</p>
          </Link>
          {/* Right teaser — suggest a tool */}
          <Link
            to="/contact?topic=suggest-seo-tool"
            className="bg-white p-6 hover:bg-zinc-50 transition-colors group"
            data-testid="seo-tools-suggest"
          >
            <div className="flex items-center gap-3 mb-2">
              <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-amber-50 text-amber-600">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M12 5v14M5 12h14"/>
                </svg>
              </span>
              <div className="text-[10px] font-mono uppercase tracking-widest text-amber-600">Suggest a tool</div>
            </div>
            <h3 className="font-display text-lg font-black tracking-tight mb-1">Want a new tool?</h3>
            <p className="text-sm text-zinc-600 leading-relaxed">Tell us which SEO metric you wish were free. We ship the top-voted one within 2 weeks.</p>
          </Link>
        </div>
      </div>
    </section>
  );
};

/* Utility widgets showcase — Phase 3 embeddables (weather, crypto,
 * countdown, globe, threads, AI chat, etc.). Mirror styling to
 * SeoToolsShowcase so both sections feel like a unified product hub.
 *
 * The grid lists 11 real widgets + 1 "Suggest a widget" CTA tile so the
 * 4×3 layout never shows a half-empty trailing row. As we ship a new
 * widget it replaces the CTA in the grid (and the CTA shifts down one). */
export const UtilityWidgetsShowcase = () => {
  const items = [
    { slug: "weather", title: "Live Weather Widget", desc: "Open-Meteo · no API key · 6 themes.", tag: "Free · viral" },
    { slug: "crypto-ticker", title: "Crypto Ticker Widget", desc: "BTC/ETH/SOL/etc with 24h change. CoinGecko.", tag: "" },
    { slug: "stock-ticker", title: "Stock Ticker Widget", desc: "AAPL, MSFT, GOOGL — live quotes.", tag: "" },
    { slug: "countdown", title: "Countdown Widget", desc: "Black Friday, Christmas, custom date. Auto-renews.", tag: "Seasonal" },
    { slug: "visitor-globe", title: "Visitor Globe Widget", desc: "Rotating 3D globe or flat map. Glowing dots.", tag: "Most viral" },
    { slug: "threads", title: "Comments Widget", desc: "5 KB Disqus killer. 8 themes · markdown · reactions.", tag: "Disqus killer" },
    { slug: "verified-traffic", title: "Verified Traffic Badge", desc: "Anti-fraud media-kit trust seal.", tag: "$$$" },
    { slug: "ask-site", title: "AI Chat Widget", desc: "GPT-5.2 chat bubble. Indexes your page.", tag: "AI" },
    { slug: "youtube-live", title: "YouTube Live Status Badge", desc: "🔴 LIVE NOW · auto-detects broadcasts.", tag: "" },
    { slug: "twitch-live", title: "Twitch Live Status Badge", desc: "🟣 LIVE · viewer count + game name.", tag: "" },
    { slug: "page-speed-badge", title: "Page Speed Badge", desc: "'This site loads in 412 ms' brag widget.", tag: "Dev fav" },
    { slug: "clicks-heatmap", title: "Clicks Heatmap Widget", desc: "See visitor clicks layered on every page. Dashboard view.", tag: "New · 🔥" },
  ];
  return (
    <section id="utility-widgets" className="bg-white border-y border-zinc-200">
      <div className="max-w-7xl mx-auto px-6 py-24 lg:py-32">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 mb-3">/ free utility widgets</div>
        <div className="grid lg:grid-cols-12 gap-8 mb-12">
          <h2 className="lg:col-span-7 font-display text-4xl lg:text-5xl font-black tracking-tighter">
            {widgetCountWord()} free utility widgets. <span className="text-klein">One copy-paste line.</span>
          </h2>
          <p className="lg:col-span-5 text-zinc-600 leading-relaxed self-end">
            Live weather (replaces Wunderground). Crypto + stock tickers (replaces Yahoo Finance). Countdown timer.
            Visitor globe. Clicks heatmap (replaces Hotjar / Crazy Egg, free forever). Anonymous comments
            (replaces Disqus). AI chat bubble. YouTube/Twitch live pills. Pick a theme, copy a script tag, done.
          </p>
        </div>
        {/* Exactly 12 items in a 4×3 grid — no orphan row, no grey filler. */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-200">
          {items.map((t) => (
            <Link
              key={t.slug}
              to={`/widgets/${t.slug}`}
              className="bg-white p-6 hover:bg-[#0A0A0A] hover:text-white transition-colors group block"
              data-testid={`home-util-widget-${t.slug}`}
            >
              {t.tag && (
                <div className="text-[10px] font-mono uppercase tracking-widest text-klein group-hover:text-emerald-300 mb-3">
                  {t.tag}
                </div>
              )}
              <h3 className="font-display text-xl font-black tracking-tight mb-2">{t.title}</h3>
              <p className="text-sm text-zinc-600 group-hover:text-zinc-100 leading-relaxed">{t.desc}</p>
            </Link>
          ))}
        </div>
        <div className="mt-8 text-center">
          <Link to="/widgets" className="inline-flex items-center gap-2 px-6 py-3 bg-klein text-white font-bold text-sm hover:bg-[#0A0A0A] transition-colors" data-testid="home-util-cta">
            See all {WIDGET_COUNT} widgets <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};
